import { useState } from "react";
import { AlertCircle, ExternalLink, Loader2, MessageCircle, Wallet } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { askItemQuestion, startPayoutOnboarding } from "../lib/api";

/**
 * Dialog shown whenever an action is blocked because a Stripe Connect
 * (Express) account is not yet connected or verified.
 *
 * Two modes:
 *   - variant="owner"  — the signed-in user is the owner and is being
 *                         blocked from accepting a request. Offers a CTA
 *                         to launch the Stripe hosted onboarding flow.
 *   - variant="renter" — the signed-in user is trying to pay, but the
 *                         other party (the owner) hasn't finished payout
 *                         setup yet. CTA is a passive dismiss.
 */
export const PayoutRequiredDialog = ({
    open,
    onOpenChange,
    variant = "owner",
    message,
    // Renter-variant only — item context so we can pre-populate a message
    // to the lender nudging them to finish payout setup.
    itemId,
    itemName,
    ownerName,
}) => {
    const [busy, setBusy] = useState(false);
    const [messaging, setMessaging] = useState(false);

    const isOwner = variant === "owner";
    const title = isOwner
        ? "Set up automatic payouts to continue"
        : "Payment temporarily unavailable";
    const description = message
        ? message
        : isOwner
        ? "Before you can accept rental requests, Lendly needs to know where to send your money. Connect a Stripe account (takes about 2 minutes) and we'll route each rental payment straight to your bank."
        : "The lender hasn't finished setting up payouts yet, so we can't process payment right now. Try again shortly — or message the lender to let them know.";

    const handleConnect = async () => {
        try {
            setBusy(true);
            const { url } = await startPayoutOnboarding();
            window.location.href = url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start Stripe onboarding");
            setBusy(false);
        }
    };

    const handleMessageLender = async () => {
        if (!itemId) {
            toast.error("Can't send — item info missing");
            return;
        }
        const body = `Hi${ownerName ? ` ${ownerName}` : ""}! I'd love to borrow your ${itemName || "item"} on Lendly, but the payment step is asking you to finish setting up payouts (Stripe Connect). Could you complete the payout setup on Lending so I can pay? Thanks!`;
        try {
            setMessaging(true);
            await askItemQuestion(itemId, {
                subject: "Payment blocked — please finish payout setup",
                body,
            });
            toast.success("Message sent to the lender", {
                description: "They'll get a nudge to finish Stripe payout setup. Check back in a few hours — once they're set up, the 'Pay now' button on this rental will unlock.",
                duration: 8000,
            });
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send message");
        } finally {
            setMessaging(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="payout-required-dialog"
                className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-0 text-[#2e3220]"
            >
                <div className="p-6">
                    <DialogHeader className="text-left">
                        <div className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl ${isOwner ? "bg-[#737a3d]" : "bg-[#B56C5A]"} text-[#F4F1EA]`}>
                            {isOwner ? (
                                <Wallet className="h-5 w-5" strokeWidth={2.5} />
                            ) : (
                                <AlertCircle className="h-5 w-5" strokeWidth={2.5} />
                            )}
                        </div>
                        <DialogTitle className="font-display text-2xl font-bold text-[#2e3220]">
                            {title}
                        </DialogTitle>
                        <DialogDescription className="text-sm leading-relaxed text-[#5a6142]">
                            {description}
                        </DialogDescription>
                    </DialogHeader>
                    {isOwner && (
                        <ul className="mt-4 space-y-2 text-xs text-[#5a6142]">
                            <li className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#737a3d]" />
                                Payments split automatically — 90% to your bank, 10% to Lendly.
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#737a3d]" />
                                Funds arrive in your bank on Stripe's rolling 2-day schedule.
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#737a3d]" />
                                You can add more listings anytime — but rentals stay paused until this is done.
                            </li>
                        </ul>
                    )}
                    <DialogFooter className="mt-6 gap-2">
                        <Button
                            variant="ghost"
                            data-testid="payout-required-dismiss"
                            onClick={() => onOpenChange(false)}
                            className="text-[#5a6142] hover:bg-[#2e3220]/5"
                        >
                            {isOwner ? "Not now" : "Close"}
                        </Button>
                        {isOwner ? (
                            <Button
                                data-testid="payout-required-connect"
                                onClick={handleConnect}
                                disabled={busy}
                                className="bg-[#737a3d] text-[#F4F1EA] hover:bg-[#5a6142]"
                            >
                                {busy ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                )}
                                Set up payouts now
                            </Button>
                        ) : (
                            itemId && (
                                <Button
                                    data-testid="payout-required-message-lender"
                                    onClick={handleMessageLender}
                                    disabled={messaging}
                                    className="bg-[#737a3d] text-[#F4F1EA] hover:bg-[#5a6142]"
                                >
                                    {messaging ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <MessageCircle className="mr-2 h-4 w-4" />
                                    )}
                                    Message the lender
                                </Button>
                            )
                        )}
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
};

/**
 * Detects whether an axios error indicates a Stripe Connect gate.
 * Returns either "owner" | "renter" | null.
 */
export const detectPayoutRequired = (err) => {
    const detail = String(err?.response?.data?.detail || "").toLowerCase();
    if (!detail) return null;
    if (detail.includes("set up automatic payouts")) return "owner";
    if (detail.includes("lender hasn't finished payout")) return "renter";
    return null;
};

export default PayoutRequiredDialog;
